import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {AllUsersStatisticsModel} from '../models/AllUsersStatisticsModel';
import {
    StatisticsRange, UserStatisticsAction, UserStatisticsActionDetail, UserStatisticsFile, UserStatisticsKind,
    UserStatisticsPage, UserStatisticsQuery
} from '../models/UserStatistics-model';

@Injectable({providedIn: 'root'})
export class UserStatisticsService {
    constructor(@Inject('API_URL') private readonly url: string, private readonly http: HttpClient) {}

    getUsers(range: StatisticsRange): Observable<AllUsersStatisticsModel[]> {
        return this.http.get<AllUsersStatisticsModel[]>(`${this.url}/userStatistics/all`, {
            params: this.rangeParams(range)
        });
    }

    getFiles(kind: UserStatisticsKind, userId: number, query: UserStatisticsQuery): Observable<UserStatisticsPage<UserStatisticsFile>> {
        const entity = kind === 'masters' ? 'masters' : 'pureOriginals';
        const search = query.search?.trim() || '';
        let params = new HttpParams()
            .set('pageNumber', Math.max(0, Math.floor(query.pageNumber)))
            .set('pageSize', Math.max(1, Math.min(100, Math.floor(query.pageSize))));
        let path = `${this.url}/userStatistics/${entity}/${userId}`;
        if (search.length >= 3) {
            const isMaster = kind === 'masters';
            path += isMaster ? '/searchByWinOlsReference' : '/searchByWinOls';
            params = params.set(isMaster ? 'winOlsReference' : 'winOls', search)
                .set('startDate', query.startDate).set('endDate', query.endDate);
        } else {
            params = params.set('start date', query.startDate).set('end date', query.endDate)
                .set('sortAttribute', query.sortAttribute).set('sortDirection', query.sortDirection);
        }
        return this.http.get<UserStatisticsPage<UserStatisticsFile>>(path, {params});
    }

    getFileActions(kind: UserStatisticsKind, userId: number, fileId: number, range: StatisticsRange): Observable<UserStatisticsAction[]> {
        const entity = kind === 'masters' ? 'masters' : 'pureOriginal';
        return this.http.get<UserStatisticsAction[]>(`${this.url}/userStatistics/${entity}/${userId}/${fileId}`, {
            params: this.rangeParams(range)
        });
    }

    private rangeParams(range: StatisticsRange): HttpParams {
        return new HttpParams().set('start date', range.startDate).set('end date', range.endDate);
    }

    getActionDetail(kind: UserStatisticsKind, userId: number, fileId: number, action: UserStatisticsAction,
                    range: StatisticsRange): Observable<UserStatisticsActionDetail | null> {
        const entity = kind === 'masters' ? 'master' : 'pureOriginal';
        return this.http.get<UserStatisticsActionDetail | null>(
            `${this.url}/userStatistics/${entity}/${userId}/${fileId}/record/${action.recordId}/status/${encodeURIComponent(action.type)}`,
            {params: new HttpParams().set('startDate', range.startDate).set('endDate', range.endDate)}
        );
    }

    errorMessage(error: HttpErrorResponse): string {
        if (error.status === 403) return 'You do not have permission to view user statistics.';
        const message = typeof error.error === 'string' ? error.error : error.error?.message;
        if (message && !message.includes('<') && message.length < 300) return message;
        return 'Could not load statistics. Please try again.';
    }
}
